import { CopyIcon, TrashIcon } from 'assets/icons';
import { CalendarDays, ClipboardList } from 'lucide-react';
import EmployeeAvatar from 'components/assign/EmployeeAvatar';
import Button from 'components/button';
import { cardIconButtonClass } from 'components/card/PanelCard';
import RichSelect from 'components/crm/ui/RichSelect';
import AppointmentForm from 'components/planning/AppointmentForm';
import IconTooltip from 'components/iconTooltip/IconTooltip';
import SlideOverBody from 'components/SlideOver/component/body';
import SlideOverFooter from 'components/SlideOver/component/footer';
import SlideOverHeader from 'components/SlideOver/component/header';
import useSlideOver from 'components/SlideOver/useSlideOver';
import StyledDateInput from 'components/styledInput/StyledDateInput';
import StyledInput from 'components/styledInput/StyledInput';
import FieldLabel from 'components/styledInput/FieldLabel';
import { companyRoleLabel } from 'constants/companyRoles';
import { taskTypeLabel } from 'constants/taskTypes';
import { fr } from 'date-fns/locale';
import { crmTaskErrorText } from 'helper/crmTaskErrors';
import { linkOptionsOf, linkPayload } from 'helper/taskLinks';
import { addDays, dayLabel, fromIsoDay, isoDay, parseClock } from 'helper/timeLabels';
import UseContext from 'hooks/useContext';
import useCrmContext from 'hooks/useCrmContext';
import useTaskPanels from 'hooks/useTaskPanels';
import { useNotifyTasksChanged } from 'hooks/useTasksChanged';
import useText from 'hooks/useText';
import { useEffect, useRef, useState } from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import { toast } from 'react-toastify';
import crmApi from 'server/crm/crmApi';
import ChoiceChips from '../ChoiceChips';
import TaskLinksField from './TaskLinksField';

registerLocale('fr-FR', fr);

// A task has a single kind: a call goes in the call list. An old Appel,
// E-mail, Visite or Réunion task becomes a plain task when saved. A new one
// may be a rendez-vous instead: the form turns into the planning's.
const TASK_TYPE = 'todo';
const MEETING = 'meeting';
const TIMES = ['9:00', '11:00', '14:00', '16:00'];
const DURATIONS = [15, 30, 45, 60, 90, 120];
const DEFAULT_DURATION = 15;

const pad = (value) => String(value).padStart(2, '0');
const nextMonday = (now) => addDays(now, ((8 - now.getDay()) % 7) || 7);

// A link is known by its kind and id: two companies may share a name, so a
// label does not tell them apart.
const linkOf = (options, type, id) => options.find((option) => option.type === type && option.id === id) ?? null;
// A contact named on a task, among the options (of its company when it has one).
const contactLink = (options, name, companyId, companyName = '') =>
  options.find((option) => option.type === 'contact' && option.label === name && (!companyId || option.companyId === companyId)) ?? {
    type: 'contact',
    id: `name:${name}`,
    label: name,
    sub: companyName,
    companyId: companyId ?? null,
  };

// A record page opens the form on its company, its group or one of its contacts.
const defaultLinks = (defaults, options) => {
  if (defaults.contactName && defaults.groupId) {
    // A contact of a group has no account: it is not among the options.
    const group = linkOf(options, 'group', defaults.groupId);
    return [...(group ? [group] : []), { type: 'contact', id: `name:${defaults.contactName}`, label: defaults.contactName, companyId: null }];
  }
  if (defaults.contactName) {
    const companyName = linkOf(options, 'company', defaults.companyId)?.label ?? '';
    return [contactLink(options, defaults.contactName, defaults.companyId, companyName)];
  }
  const record = defaults.companyId
    ? linkOf(options, 'company', defaults.companyId)
    : defaults.groupId
      ? linkOf(options, 'group', defaults.groupId)
      : null;
  return record ? [record] : [];
};

// A task without `links` (a real one) is linked to its company, its group
// and its contact; a record missing from the options keeps the task's own
// name, so that it is never dropped silently.
const linksOfTask = (task, options) => {
  if (task.links?.length) return task.links;
  return [
    task.companyId && (linkOf(options, 'company', task.companyId) ?? { type: 'company', id: task.companyId, label: task.company?.name ?? '' }),
    task.groupId && (linkOf(options, 'group', task.groupId) ?? { type: 'group', id: task.groupId, label: task.group?.name ?? '' }),
    task.contactName && contactLink(options, task.contactName, task.companyId, task.company?.name),
  ].filter(Boolean);
};

const emptyForm = (defaults, options) => ({
  title: '',
  note: '',
  dueOn: defaults.dueOn ?? isoDay(new Date()),
  time: defaults.time ?? '',
  duration: defaults.duration ?? DEFAULT_DURATION,
  durationTouched: Boolean(defaults.duration),
  assigneeId: '',
  links: defaultLinks(defaults, options),
});
const formOf = (task, options) => ({
  title: task.title,
  note: task.note ?? '',
  dueOn: task.dueOn,
  time: task.dueTime ? task.dueTime.slice(0, 5).replace(/^0/, '') : '',
  duration: task.durationMin ?? DEFAULT_DURATION,
  durationTouched: true,
  assigneeId: task.assigneeId,
  links: linksOfTask(task, options),
});

/**
 * « Nouvelle tâche » and « Modifier la tâche » (slide-over `task_form`, with
 * `{ taskId, defaults }` in `objID`): the title, written by the user (no AI),
 * its description, the day and the time with their shortcuts, the length, the
 * colleague, the records it is linked to (contacts, companies, groups, temp
 * workers). ⌘ or Ctrl + Entrée saves. Why the save button is grey is written
 * beside it. A new task goes to `defaults.assigneeId` if given, else to me.
 * « Type » (a new one only) turns it into a rendez-vous (`defaults.type`
 * 'meeting' opens it so): the planning's AppointmentForm, with what was typed,
 * linked to the first company and the first group chosen, the week beside it.
 */
const TaskFormPanel = () => {
  const { objID } = UseContext();
  const { taskId = null, defaults = {} } = objID && typeof objID === 'object' ? objID : {};
  const { language, text } = useText('taskForm');
  const { text: tasksText } = useText('tasks');
  const { text: fieldsText } = useText('appointmentFields');
  const { call, me } = useCrmContext();
  const { toggleSlideOver } = useSlideOver();
  const { openTask } = useTaskPanels();
  const notify = useNotifyTasksChanged();
  const [task, setTask] = useState(null);
  const [team, setTeam] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [linkOptions, setLinkOptions] = useState([]);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [kind, setKind] = useState(defaults.type === MEETING ? MEETING : TASK_TYPE);
  const body = useRef(null);
  const submitRef = useRef(null);
  const editing = Boolean(taskId);
  const fail = (error) => {
    const message = crmTaskErrorText(error, tasksText);
    if (message) toast.error(message);
  };

  useEffect(() => {
    if (!me) return;
    const { taskId: id = null, defaults: given = {} } = objID && typeof objID === 'object' ? objID : {};
    setForm(null);
    setKind(given.type === MEETING ? MEETING : TASK_TYPE);
    Promise.all([
      id ? call(crmApi.getTask, { id }) : Promise.resolve(null),
      call(crmApi.getTeam),
      call(crmApi.getContacts),
    ]).then(
      ([found, people, records]) => {
        const options = linkOptionsOf(records);
        setTask(found);
        setTeam(people);
        setContacts(records);
        setLinkOptions(options);
        setForm(found ? formOf(found, options) : { ...emptyForm(given, options), assigneeId: given.assigneeId || me.id });
      },
      fail,
    );
  }, [call, me, objID]);

  // A new task opens on its title, as « N » then typing expects.
  const shown = Boolean(form);
  useEffect(() => {
    if (shown && !editing) body.current?.querySelector('input')?.focus({ preventScroll: true });
  }, [shown, editing]);

  useEffect(() => {
    const onKey = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
        event.preventDefault();
        submitRef.current?.();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const close = () => toggleSlideOver();
  // « Annuler » goes back to the task it edits.
  const cancel = () => (editing ? openTask(taskId) : close());

  if (!form) {
    return (
      <>
        <SlideOverHeader hide={close} closeLabel={text('close')}>
          {text(editing ? 'editTitle' : 'newTitle')}
        </SlideOverHeader>
        <SlideOverBody>
          <div className="mx-auto mt-16 h-6 w-6 animate-spin rounded-full border-2 border-yellow-500 border-t-transparent" />
        </SlideOverBody>
      </>
    );
  }

  const set = (field, value) => setForm((current) => ({ ...current, [field]: value }));
  const clock = form.time.trim() ? parseClock(form.time) : null;
  const timeOk = !form.time.trim() || Boolean(clock);
  let blocker = null;
  if (!form.title.trim()) blocker = text('blockTitle');
  else if (!form.note.trim()) blocker = text('blockDescription');
  else if (!timeOk) blocker = text('blockTime');
  const now = new Date();
  const monday = nextMonday(now);

  const firstLink = (type) => form.links.find((link) => link.type === type) ?? null;
  const company = firstLink('company');
  const contact = firstLink('contact');
  const contactOption = contact
    ? contacts.find((option) => (option.userId ?? option.key) === contact.id && (option.companyId ?? null) === (contact.companyId ?? null))
    : null;
  // A contact kept from the task, not among the options, keeps its role and phone.
  const keptContact = !contactOption && contact && task && contact.label === task.contactName ? task : null;
  const fields = () => ({
    type: TASK_TYPE,
    title: form.title.trim(),
    note: form.note.trim(),
    dueOn: form.dueOn,
    dueTime: clock ? `${pad(clock[0])}:${pad(clock[1])}` : null,
    durationMin: Number(form.duration),
    assigneeId: form.assigneeId,
    // The api keeps the links, and deduces companyId, groupId and contactName
    // from them: it ignores these old fields when `links` is present. They are
    // still sent, for the demo and for the detail of a task.
    links: form.links.map(linkPayload),
    companyId: company?.id ?? contact?.companyId ?? null,
    groupId: firstLink('group')?.id ?? null,
    contactName: contact?.label ?? '',
    contactRole: contactOption
      ? typeof contactOption.companyRole === 'number'
        ? companyRoleLabel(language, contactOption.companyRole)
        : (contactOption.role ?? '')
      : (keptContact?.contactRole ?? ''),
    contactDetail: contactOption ? (contactOption.phone ?? '') : (keptContact?.contactDetail ?? ''),
  });

  // Out of today, the toast says when; given to a colleague, to whom.
  const savedMessage = () => {
    const when =
      form.dueOn === isoDay(now)
        ? ''
        : form.dueOn === isoDay(addDays(now, 1))
          ? text('whenTomorrow')
          : text('whenDay', { day: dayLabel(fromIsoDay(form.dueOn)).toLowerCase() });
    const colleague = form.assigneeId !== me.id && form.assigneeId !== (task?.assigneeId ?? me.id);
    if (colleague) {
      const person = team.find((member) => member.id === form.assigneeId);
      return `${text('handed', { name: `${person.firstname} ${person.lastname}`.trim() })}${when}`;
    }
    return editing ? text('saved') : `${text('addedTodo')}${when}`;
  };

  const submit = async () => {
    if (blocker || busy) return;
    setBusy(true);
    try {
      const saved = editing
        ? await call(crmApi.updateTask, { id: taskId, fields: fields() })
        : await call(crmApi.createTask, { fields: fields() });
      // Added from the « Terminées » column of the kanban: done at once.
      if (!editing && defaults.done) await call(crmApi.setTaskDone, { id: saved.id, done: true });
      toast.success(savedMessage());
      notify();
      close();
    } catch (error) {
      fail(error);
    } finally {
      setBusy(false);
    }
  };
  submitRef.current = submit;

  const remove = async () => {
    try {
      await call(crmApi.deleteTask, { id: taskId });
      toast.success(text('deleted'));
      notify();
      close();
    } catch (error) {
      fail(error);
    }
  };
  const duplicate = async () => {
    try {
      await call(crmApi.createTask, { fields: { ...fields(), title: text('copyOf', { title: form.title }) } });
      toast.success(text('duplicated'));
      notify();
      close();
    } catch (error) {
      fail(error);
    }
  };

  // Tâche or Rendez-vous, for a new one only.
  const typeField = !editing && (
    <RichSelect
      id="task-type"
      label={text('typeLabel')}
      value={kind}
      options={[
        { value: TASK_TYPE, label: taskTypeLabel(language, TASK_TYPE), icon: ClipboardList, description: text('typeHelp_todo') },
        { value: MEETING, label: text('typeMeeting'), icon: CalendarDays, iconClass: 'text-primary', description: text('typeHelp_meeting') },
      ]}
      hint={text(`typeHint_${kind}`)}
      onChange={setKind}
    />
  );

  if (kind === MEETING && !editing) {
    // What was typed comes along: the title, the description, the day and
    // the time, the first company (or the contact's) and the contact.
    const start = clock ? `${pad(clock[0])}:${pad(clock[1])}` : '10:00';
    const [hours, minutes] = start.split(':').map(Number);
    const endMinutes = Math.min(hours * 60 + minutes + 60, 23 * 60 + 59);
    const companyId = company?.id ?? contact?.companyId ?? null;
    const companyName = company?.label ?? contact?.sub ?? '';
    const groupId = firstLink('group')?.id ?? null;
    const companyContacts = companyId
      ? contacts.filter((option) => option.companyId === companyId && option.contact).map((option) => ({ name: option.contact }))
      : [];
    return (
      <AppointmentForm
        mode="create"
        layout="panel"
        prepend={typeField}
        record={companyId ? { companyId, name: companyName, contacts: companyContacts, contactName: contact?.label ?? '' } : null}
        groupId={groupId}
        initial={{
          title: form.title.trim(),
          description: form.note.trim(),
          startDay: form.dueOn,
          endDay: form.dueOn,
          start,
          end: `${pad(Math.floor(endMinutes / 60))}:${pad(endMinutes % 60)}`,
          ...(form.assigneeId && form.assigneeId !== me.id ? { ownerId: form.assigneeId } : {}),
        }}
        header={
          <SlideOverHeader hide={close} closeLabel={text('close')}>
            {fieldsText('createTitle')}
          </SlideOverHeader>
        }
        onSaved={() => {
          notify();
          close();
        }}
        onCancel={close}
      />
    );
  }

  return (
    <>
      <SlideOverHeader hide={close} closeLabel={text('close')}>
        <span className="block">{text(editing ? 'editTitle' : 'newTitle')}</span>
        <span className="mt-1 block text-sm font-normal text-gray-500">
          {editing
            ? text('createdBy', {
                name: task.createdByPerson ? `${task.createdByPerson.firstname} ${task.createdByPerson.lastname}`.trim() : '',
                day: dayLabel(new Date(task.createdAt)).toLowerCase(),
              })
            : text('newSubtitle')}
        </span>
      </SlideOverHeader>
      <SlideOverBody>
        <div ref={body} className="flex flex-col gap-5">
          {typeField}
          <StyledInput
            type="text"
            label={text('titleLabel')}
            value={form.title}
            maxLength={120}
            onChange={(event) => set('title', event.target.value)}
          />
          <StyledInput
            type="textarea"
            label={text('noteLabel')}
            value={form.note}
            maxLength={2000}
            onChange={(event) => set('note', event.target.value)}
          />
          <div className="grid grid-cols-2 gap-x-3 gap-y-2">
            <DatePicker
              dateFormat="dd/MM/yyyy"
              selected={fromIsoDay(form.dueOn)}
              onChange={(date) => date && set('dueOn', isoDay(date))}
              locale="fr-FR"
              popperPlacement="bottom"
              customInput={<StyledDateInput label={text('dayLabel')} />}
            />
            <StyledInput
              type="text"
              label={text('timeLabel')}
              placeholder="9:30"
              value={form.time}
              error={timeOk ? null : text('timeError')}
              onChange={(event) => set('time', event.target.value)}
            />
            <ChoiceChips
              options={[
                [isoDay(now), text('today')],
                [isoDay(addDays(now, 1)), text('tomorrow')],
                [isoDay(monday), text('monday', { day: monday.getDate() })],
              ]}
              value={form.dueOn}
              onPick={(day) => set('dueOn', day)}
            />
            <ChoiceChips
              options={TIMES.map((time) => [time, time])}
              value={form.time}
              onPick={(time) => set('time', form.time === time ? '' : time)}
            />
          </div>
          <div className="flex flex-col gap-2">
            <FieldLabel label={text('durationLabel')} />
            <ChoiceChips
              options={DURATIONS.map((minutes) => [minutes, text(`duration_${minutes}`)])}
              value={Number(form.duration)}
              onPick={(minutes) => setForm((current) => ({ ...current, duration: minutes, durationTouched: true }))}
            />
          </div>
          <StyledInput
            type="select"
            label={text('assigneeLabel')}
            value={form.assigneeId}
            imgFront={<EmployeeAvatar employee={team.find((member) => member.id === form.assigneeId)} size={18} />}
            list={team.map((member) => ({
              value: member.id,
              label:
                member.id === me.id
                  ? text('me', { name: `${member.firstname} ${member.lastname}`.trim() })
                  : `${member.firstname} ${member.lastname}`.trim(),
            }))}
            onChange={(event) => set('assigneeId', event.target.value)}
          />
          <TaskLinksField
            label={text('linksLabel')}
            placeholder={text('linksPlaceholder')}
            hint={text('linksHint')}
            options={linkOptions}
            value={form.links}
            onChange={(links) => set('links', links)}
          />
        </div>
      </SlideOverBody>
      <div className="flex-shrink-0 border-t border-gray-100">
        <SlideOverFooter>
          {editing && (
            <div className="flex items-center gap-2">
              <IconTooltip label={text('delete')}>
                <button type="button" aria-label={text('delete')} onClick={remove} className={cardIconButtonClass}>
                  <TrashIcon wh={18} color="#DC2626" />
                </button>
              </IconTooltip>
              <IconTooltip label={text('duplicate')}>
                <button type="button" aria-label={text('duplicate')} onClick={duplicate} className={cardIconButtonClass}>
                  <CopyIcon wh={18} />
                </button>
              </IconTooltip>
            </div>
          )}
          <span className="mr-auto text-xs text-gray-500">{blocker}</span>
          <Button color="SECONDARY" size="SM" onClick={cancel}>
            {text('cancel')}
          </Button>
          <Button color={blocker || busy ? 'DISABLED' : 'PRIMARY'} size="SM" disabled={Boolean(blocker) || busy} onClick={submit}>
            {busy ? text('busy') : text(editing ? 'save' : 'ctaTodo')}
          </Button>
        </SlideOverFooter>
      </div>
    </>
  );
};

export default TaskFormPanel;
